<script setup lang="ts">
import { useFetchGeneral } from '~/api/service/general/index.service'
import BlockFlow from '~/components/@pages/home/BlockFlow.vue'
import HomeCardTitle from '~/components/@pages/home/HomeCardTitle.vue'
import HomeChartTitle from '~/components/@pages/home/HomeChartTitle.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import IconTooltip from '~/components/tooltip/IconTooltip.vue'
import { HOME_CHART_TOOLTIP, HOME_REFETCH_PARAMS } from '~/constants/general.const'
import { IMAGE_BASE_URL } from '~/constants/image.constant'
import { generalParamType } from '~/types/general.type'

const { data: blockOnlyData } = useFetchGeneral([ generalParamType.BLOCK ])
const { data: refetchData, isLoading } = useFetchGeneral(HOME_REFETCH_PARAMS)

const data = ref(refetchData.value)
const isSameLatestBlock = ref(false)

watch([ blockOnlyData, refetchData ], ([ newBlockOnlyData, newRefetchData ], [ oldBlockOnlyData, oldRefetchData ]) => {
  if (!oldBlockOnlyData && !oldRefetchData) {
    return
  }
  if (newBlockOnlyData === oldBlockOnlyData) {
    isSameLatestBlock.value = data.value?.FBI.OBJ.LBNO === newRefetchData?.FBI.OBJ.LBNO
    data.value = newRefetchData
    return
  }
  if (newRefetchData === oldRefetchData) {
    isSameLatestBlock.value = data.value?.FBI.OBJ.LBNO === newBlockOnlyData?.FBI.OBJ.LBNO
    data.value = newBlockOnlyData
    return
  }
}, { deep: true, immediate: true })

const maxTransactionCount = computed(() => {
  if (!data.value) {
    return 0
  }
  return Math.max(...data.value.FBI.OBJ.RBLIST.map(block => parseInt(block.TRXCNT)))
})

const firstBlockList = computed(() => data.value?.FBI.OBJ.RBLIST.slice(0, 5) || [])
const secondBlockList = computed(() => data.value?.FBI.OBJ.RBLIST.slice(-5) || [])
</script>

<template>
  <div>
    <CustomSuspense :is-loading="isLoading">
      <template #loading>
        <img
          :src="`${IMAGE_BASE_URL}/skeleton/home/block-chain.png`"
          alt="line chart skeleton"
        >
      </template>
      <template #default>
        <Card
          v-if="data"
          class="flex flex-col justify-start p-5 pb-2"
        >
          <div class="flex items-center gap-x-2 text-primary/40">
            <HomeCardTitle>
              <div class="flex items-center gap-x-1">
                Average Block Created
                <IconTooltip :tooltip="HOME_CHART_TOOLTIP[generalParamType.BLOCK]" />
                :
              </div>
              <strong class="font-normal text-primary">{{
                data.FBI.OBJ.BAVGCT
              }}</strong><span> secs</span>
            </HomeCardTitle>
          </div>
          <div class="mb-4 mt-8 flex flex-col gap-y-2">
            <HomeChartTitle
              title="Block Number"
              :tooltip="HOME_CHART_TOOLTIP.BLOCK_NUMBER"
            />
            <strong class="text-3xl text-primary">{{ data.FBI.OBJ.LBNO }}</strong>
          </div>
          <BlockFlow
            v-for="(list, index) in [firstBlockList, secondBlockList]"
            :key="index"
            :list="list"
            :max-transaction-count="maxTransactionCount"
            :is-first="!index"
            :is-same-latest-block="isSameLatestBlock"
          />
        </Card>
      </template>
    </CustomSuspense>
  </div>
</template>

<style scoped>

</style>